import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-leaflet';

import HeatmapDatePopup from './HeatmapDatePopup';
import FilterDatePopup from './FilterDatePopup';
import ArchiveDatePopup from './ArchiveDatePopup';

import Toggle from 'components/Common/Toggle';
import { ArchiveDevicesResponse, DevicesOnMapResponse, FloorPlan } from 'api/InteractiveMap/types';

interface FiltersState {
  mct: boolean;
  srbm: boolean;
  srst: boolean;
  smst: boolean;
  vulnerabilities: boolean;
  heatmap: boolean;
  archive: boolean;
  filter: boolean;
  encounters: boolean;
  heatmapMct: boolean;
  heatmapSRBM: boolean;
  heatmapSRST: boolean;
  heatmapSMST: boolean;
  srbmFilter: string;
  snapsFilter: string;
}

interface MapSettingsProps {
  devices?: DevicesOnMapResponse | ArchiveDevicesResponse;
  filters: FiltersState;
  onFilterChange: <K extends keyof FiltersState>(filter: K, value: FiltersState[K]) => void;
  heatmapDateStart: string;
  heatmapDateEnd: string;
  setHeatmapDateStart: (date: string) => void;
  setHeatmapDateEnd: (date: string) => void;
  onConfirmDates?: (start: string, end: string) => void;
  isHeatmapLoading?: boolean;
  isHeatmapSRBMLoading?: boolean;
  isHeatmapSMSTLoading?: boolean;
  isHeatmapSRSTLoading?: boolean;
  currentPlan: FloorPlan;
  isHeatmapPopupOpen?: boolean;
  setIsHeatmapPopupOpen?: (isOpen: boolean) => void;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  setIsArchiveFilterActive: (isActive: boolean) => void;
  setArchiveMode?: (mode: 'date' | 'period') => void;
  devicesLoading?: boolean;
}

const MapSettings: React.FC<MapSettingsProps> = ({
  devices,
  filters,
  onFilterChange,
  heatmapDateStart,
  heatmapDateEnd,
  setHeatmapDateStart,
  setHeatmapDateEnd,
  onConfirmDates,
  isHeatmapLoading,
  isHeatmapSRBMLoading,
  isHeatmapSMSTLoading,
  isHeatmapSRSTLoading,
  currentPlan,
  isHeatmapPopupOpen,
  setIsHeatmapPopupOpen,
  isOpen: externalIsOpen,
  setIsOpen: externalSetIsOpen,
  setIsArchiveFilterActive,
  setArchiveMode,
  devicesLoading,
}) => {
  const [localIsOpen, setLocalIsOpen] = useState(externalIsOpen || false);
  const [showHeatmapPopup, setShowHeatmapPopup] = useState(isHeatmapPopupOpen || false);
  const [archivePopupOpen, setArchivePopupOpen] = useState(filters.archive);
  const [isDatePopup, setIsDatePopup] = useState(false);
  const [pendingStart, setPendingStart] = useState(heatmapDateStart);
  const [pendingEnd, setPendingEnd] = useState(heatmapDateEnd);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const map = useMap();
  const [panelOrder, setPanelOrder] = useState<('heatmap' | 'archive' | 'filter')[]>([]);

  const isMenuOpen = externalIsOpen !== undefined ? externalIsOpen : localIsOpen;

  const handleToggleMenu = (newValue: boolean) => {
    setLocalIsOpen(newValue);
    if (externalSetIsOpen) {
      externalSetIsOpen(newValue);
    }
  };
  useEffect(() => {
    setPanelOrder(prev => {
      const nextOrder = [...prev];

      if (filters.archive && !nextOrder.includes('archive')) nextOrder.push('archive');
      if (filters.heatmap && !nextOrder.includes('heatmap')) nextOrder.push('heatmap');
      if (filters.filter && !nextOrder.includes('filter')) nextOrder.push('filter');

      return nextOrder.filter(item => {
        if (item === 'archive') return filters.archive;
        if (item === 'heatmap') return filters.heatmap;
        if (item === 'filter') return filters.filter;
        return true;
      });
    });
  }, [filters.archive, filters.heatmap, filters.filter]);
  const updatePanelOrder = (panel: 'heatmap' | 'archive' | 'filter', isOpen: boolean) => {
    setPanelOrder(prev => {
      if (isOpen) {
        if (prev.includes(panel)) return prev;
        return [...prev, panel];
      } else {
        return prev.filter(p => p !== panel);
      }
    });
  };
  useEffect(() => {
    if (!isMenuOpen) {
      map.scrollWheelZoom.enable();
      map.dragging.enable();
    }
  }, [isMenuOpen, map]);

  useEffect(() => {
    return () => {
      map.scrollWheelZoom.enable();
      map.dragging.enable();
    };
  }, [map]);
  useEffect(() => {
    if (isHeatmapPopupOpen !== undefined) {
      setShowHeatmapPopup(isHeatmapPopupOpen);
      updatePanelOrder('heatmap', isHeatmapPopupOpen);
    }
  }, [isHeatmapPopupOpen]);

  useEffect(() => {
    if (setIsHeatmapPopupOpen) {
      setIsHeatmapPopupOpen(showHeatmapPopup);
    }
  }, [showHeatmapPopup, setIsHeatmapPopupOpen]);

  useEffect(() => {
    setPendingStart(heatmapDateStart);
    setPendingEnd(heatmapDateEnd);
  }, [heatmapDateStart, heatmapDateEnd]);

  useEffect(() => {
    setArchivePopupOpen(filters.archive);
    updatePanelOrder('archive', filters.archive);
  }, [filters.archive]);

  useEffect(() => {
    updatePanelOrder('filter', filters.filter);
  }, [filters.filter]);

  const today = new Date();
  const minDate = new Date(currentPlan.add_date);
  const maxDate = today;

  const dateToDays = (dateStr: string) => {
    const d = new Date(dateStr);
    return Math.round((d.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const daysToDate = (days: number) => {
    const d = new Date(minDate.getTime() + days * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  };

  const minDays = 0;
  const maxDays = dateToDays(maxDate.toISOString().slice(0, 10));
  let sliderValue: [number, number];
  const startVal = dateToDays(pendingStart);
  const endVal = dateToDays(pendingEnd);
  if (isNaN(startVal) || isNaN(endVal)) {
    sliderValue = [minDays, maxDays];
  } else {
    sliderValue = [startVal, endVal];
  }

  const handleSliderChange = (vals: [number, number]) => {
    if (vals[0] !== dateToDays(pendingStart)) {
      setPendingStart(daysToDate(vals[0]));
    }
    if (vals[1] !== dateToDays(pendingEnd)) {
      setPendingEnd(daysToDate(vals[1]));
    }
  };

  const handleStartDateChange = (newStart: string) => {
    const startDate = new Date(newStart);
    const endDate = new Date(pendingEnd);
    if (startDate > endDate) {
      setPendingEnd(newStart);
    }
    setPendingStart(newStart);
  };

  const handleEndDateChange = (newEnd: string) => {
    const startDate = new Date(pendingStart);
    const endDate = new Date(newEnd);
    if (endDate < startDate) {
      setPendingStart(newEnd);
    }
    setPendingEnd(newEnd);
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        bottom: '10px',
        zIndex: 1000,
        width: '260px',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
      }}
    >
      <button
        className="map-dropdown-toggle"
        title={t('MapSettings.mapLayers')}
        onClick={() => handleToggleMenu(!isMenuOpen)}
        style={{ position: 'relative', pointerEvents: 'auto', alignSelf: 'flex-end', marginBottom: '8px' }}
      >
        {t('MapSettings.layers')} ▾
      </button>

      {isMenuOpen && (
        <div
          className="custom-map-sidebar-scrollbar"
          onMouseEnter={() => {
            map.scrollWheelZoom.disable();
            map.dragging.disable();
          }}
          onMouseLeave={() => {
            map.scrollWheelZoom.enable();
            map.dragging.enable();
          }}
          onWheel={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            overflowY: 'auto',
            width: '100%',
            pointerEvents: 'auto',
            paddingBottom: '10px',
            boxSizing: 'border-box',
          }}
        >
          <div className="bg-white border" style={{ borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="border-bottom border-1">
              <div style={{ padding: '8px 16px', paddingBottom: '0px' }}>
                <div className="d-flex justify-content-between align-items-center  pb-1">
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#3a3a6a' }}>{t('MapSettings.mapLayers')}</div>
                  <button
                    className="btn btn-light waves-effect btn-sm fs-5 fw-semibold"
                    style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
                    onClick={() => handleToggleMenu(false)}
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              </div>
            </div>
            <div className="plan-item">
              {devices && 'mct' in devices && devices.mct?.length ? (
                <div className="mb-1">
                  <Toggle
                    checked={filters.mct}
                    onChange={checked => onFilterChange('mct', checked)}
                    label={t('MapSettings.multiCatchTraps')}
                    size="sm"
                  />
                </div>
              ) : null}
              {devices && 'srbm' in devices && devices.srbm?.length ? (
                <div className="mb-1">
                  <Toggle
                    checked={filters.srbm}
                    onChange={checked => onFilterChange('srbm', checked)}
                    label={t('MapSettings.smartRodentBaitModules')}
                    size="sm"
                  />
                </div>
              ) : null}
              {devices && 'smst' in devices && devices.smst?.length ? (
                <div className="mb-1">
                  <Toggle
                    checked={filters.smst}
                    onChange={checked => onFilterChange('smst', checked)}
                    label={t('MapSettings.snapTrapMouse')}
                    size="sm"
                  />
                </div>
              ) : null}
              {devices && 'srst' in devices && devices.srst?.length ? (
                <div className="mb-1">
                  <Toggle
                    checked={filters.srst}
                    onChange={checked => onFilterChange('srst', checked)}
                    label={t('MapSettings.snapTrapRat')}
                    size="sm"
                  />
                </div>
              ) : null}
              <div className="mb-1">
                <Toggle
                  checked={filters.vulnerabilities}
                  onChange={checked => onFilterChange('vulnerabilities', checked)}
                  label={t('MapSettings.pestConduciveConditions')}
                  size="sm"
                />
              </div>
              <div className="mb-1">
                <Toggle
                  checked={filters.encounters}
                  onChange={checked => onFilterChange('encounters', checked)}
                  label={t('MapSettings.pestSightings')}
                  size="sm"
                />
              </div>
              <div className="mb-1">
                <Toggle
                  checked={filters.heatmap}
                  onChange={checked => {
                    onFilterChange('heatmap', checked);
                    setShowHeatmapPopup(checked);
                    updatePanelOrder('heatmap', checked);

                    if (checked) {
                      onFilterChange('heatmapMct', true);
                      onFilterChange('heatmapSRBM', true);
                      onFilterChange('heatmapSRST', true);
                      onFilterChange('heatmapSMST', true);
                    }
                  }}
                  label={t('MapSettings.heatmap')}
                  size="sm"
                />
              </div>
              <div className="mb-1">
                <Toggle
                  checked={filters.archive}
                  onChange={checked => {
                    onFilterChange('archive', checked);
                    setArchivePopupOpen(checked);
                    updatePanelOrder('archive', checked);
                    if (!checked) {
                      setIsArchiveFilterActive(false);
                    }
                  }}
                  label={t('MapSettings.archive')}
                  size="sm"
                />
              </div>

              <div className="mb-1">
                <Toggle
                  checked={filters.filter}
                  onChange={checked => {
                    onFilterChange('filter', checked);
                    updatePanelOrder('filter', checked);
                  }}
                  label={t('MapSettings.filter')}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {panelOrder.map(panelType => {
            if (panelType === 'heatmap' && showHeatmapPopup) {
              return (
                <div key="heatmap-wrapper" style={{ width: '100%' }}>
                  <HeatmapDatePopup
                    devices={devices}
                    filters={filters}
                    onFilterChange={onFilterChange}
                    pendingStart={pendingStart}
                    pendingEnd={pendingEnd}
                    setPendingStart={handleStartDateChange}
                    setPendingEnd={handleEndDateChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    minDays={minDays}
                    maxDays={maxDays}
                    sliderValue={sliderValue}
                    handleSliderChange={handleSliderChange}
                    isHeatmapLoading={isHeatmapLoading}
                    isHeatmapSRBMLoading={isHeatmapSRBMLoading}
                    isHeatmapSMSTLoading={isHeatmapSMSTLoading}
                    isHeatmapSRSTLoading={isHeatmapSRSTLoading}
                    onConfirm={() => {
                      setHeatmapDateStart(pendingStart);
                      setHeatmapDateEnd(pendingEnd);
                      if (onConfirmDates) onConfirmDates(pendingStart, pendingEnd);
                    }}
                    onClose={() => {
                      setShowHeatmapPopup(false);
                      updatePanelOrder('heatmap', false);
                      if (setIsHeatmapPopupOpen) setIsHeatmapPopupOpen(false);
                      onFilterChange('heatmap', false);
                    }}
                  />
                </div>
              );
            }

            if (panelType === 'archive' && archivePopupOpen) {
              return (
                <div key="archive-wrapper" style={{ width: '100%' }}>
                  <ArchiveDatePopup
                    isDatePopup={isDatePopup}
                    setIsDatePopup={setIsDatePopup}
                    onFilterChange={onFilterChange}
                    initialStart={heatmapDateStart}
                    initialEnd={heatmapDateEnd}
                    minDate={minDate}
                    maxDate={maxDate}
                    onConfirm={(confirmedStart, confirmedEnd, confirmedMode) => {
                      if (setArchiveMode) {
                        setArchiveMode(confirmedMode);
                      }
                      setHeatmapDateStart(confirmedStart);
                      setHeatmapDateEnd(confirmedEnd);
                      setIsArchiveFilterActive(true);
                    }}
                    onClose={() => {
                      setArchivePopupOpen(false);
                      updatePanelOrder('archive', false);
                      setIsArchiveFilterActive(false);
                      onFilterChange('archive', false);
                    }}
                    isLoading={devicesLoading}
                  />
                </div>
              );
            }

            if (panelType === 'filter' && filters.filter) {
              return (
                <div key="filter-wrapper" style={{ width: '100%' }}>
                  <FilterDatePopup
                    filters={filters}
                    onFilterChange={onFilterChange}
                    onClose={() => {
                      onFilterChange('filter', false);
                      updatePanelOrder('filter', false);
                    }}
                  />
                </div>
              );
            }

            return null;
          })}
        </div>
      )}
    </div>
  );
};

export default MapSettings;
